import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, ArrowRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { healthAPI } from '../../services/api'
import AuthShell from '../../components/auth/AuthShell'
import toast from 'react-hot-toast'

const LoginPage = () => {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const reduceMotion = useReducedMotion()

  // Live backend health, mirroring the landing hero's status badge — never a
  // hardcoded claim. `null` while probing; servicesUp === 0 hides the badge
  // rather than asserting "0/4 services live" during a real or probe outage.
  const [servicesUp, setServicesUp] = useState(null)
  useEffect(() => {
    let active = true
    healthAPI
      .getSystemStatus()
      .then((statusData) => {
        if (!active) return
        const states = ['auth', 'models', 'schema', 'predictions'].map((k) => statusData?.[k]?.status)
        setServicesUp(states.filter((s) => s && s !== 'down').length)
      })
      .catch(() => {
        if (active) setServicesUp(0)
      })
    return () => {
      active = false
    }
  }, [])
  const showStatusBadge = servicesUp === null || servicesUp > 0
  const statusDot = servicesUp === null ? 'bg-gray-300' : servicesUp >= 3 ? 'bg-success-500' : 'bg-warning-500'

  // Where to land after a successful login. `/app/dashboard`, not `/dashboard`
  // — the latter isn't a route. `state.from` is set by the auth guard in
  // App.jsx to resume a deep link after login; only in-app `/app/*` paths are
  // accepted so this can't become an open redirect if `from` gets wired to
  // something more exposed later.
  const requested = location.state?.from?.pathname
  const from = requested?.startsWith('/app/') ? requested : '/app/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const result = await login(data.email, data.password)

      if (result.success) {
        toast.success(`Welcome back, ${result.user.firstName}!`)

        // Post-login landing (settings consolidation, #31; path updated by
        // the Settings IA rebuild, 2026-07): a resumed deep link always
        // wins; otherwise admins land on System overview (/app/system,
        // formerly /app/admin, then /app/settings/system) and everyone
        // else on the dashboard. `from` already resolves to the requested
        // /app/* deep link or /app/dashboard.
        const roles = result.user.roles || []
        const isAdmin = roles.includes('super_admin') || roles.includes('admin')
        const redirectPath = requested ? from : (isAdmin ? '/app/system' : from)
        navigate(redirectPath, { replace: true })
      } else {
        // Surface the server's actual reason, inline and as a toast. A 401 from
        // /auth/login must not trip the global "session expired" interceptor
        // in api.js, which hard-reloads the page before this can render.
        const message = result.error || 'Invalid email or password'
        setError('root', { type: 'manual', message })
        toast.error(message)
      }
    } catch (error) {
      // AuthContext.login() never rethrows — it catches every network/HTTP
      // failure and resolves {success:false, error} — so anything landing
      // here is a bug in our own code, not a failed login.
      console.error('Unexpected login error:', error)
      const message = 'Something went wrong signing you in. Please try again.'
      setError('root', { type: 'manual', message })
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell>
      <div>
        <div className="mb-7 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-1.5 text-sm text-gray-600">
            Access the Integrated Early Warning and Response System
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Global error */}
          <AnimatePresence>
            {errors.root && (
              <motion.div
                initial={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0 }}
                className="alert-danger"
                role="alert"
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-danger-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{errors.root.message}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Email field */}
          <div>
            <label htmlFor="email" className="field-label">
              Email address
            </label>
            <div className="mt-2 relative">
              <Mail className="input-icon" strokeWidth={1.75} />
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                id="email"
                type="email"
                autoComplete="email"
                disabled={isLoading}
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={`input pl-11 ${errors.email ? 'input-error' : ''}`}
                placeholder="you@example.com"
              />
            </div>
            {errors.email && (
              <p id="email-error" className="field-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>{errors.email.message}</span>
              </p>
            )}
          </div>

          {/* Password field */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="field-label">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 link-focus rounded-md"
              >
                Forgot password?
              </Link>
            </div>
            <div className="mt-2 relative">
              <Lock className="input-icon" strokeWidth={1.75} />
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={isLoading}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={`input pl-11 pr-11 ${errors.password ? 'input-error' : ''}`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="input-trailing-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                ) : (
                  <Eye className="h-4 w-4" strokeWidth={1.75} />
                )}
              </button>
            </div>
            {errors.password && (
              <p id="password-error" className="field-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>{errors.password.message}</span>
              </p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              disabled={isLoading}
              className="checkbox"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-brand btn-lg w-full gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                Signing in&hellip;
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </>
            )}
          </button>
        </form>

        {/* Register link */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Don&apos;t have an account?</span>
            </div>
          </div>

          <div className="mt-5">
            <Link
              to="/register"
              className="btn-secondary btn-lg w-full"
            >
              Create new account
            </Link>
          </div>
        </div>
      </div>

      {/* Live probe, same source as the landing hero's badge — renders nothing
          when servicesUp === 0 rather than a false "operational" dot. */}
      {showStatusBadge && (
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <span className="relative flex h-2 w-2">
              {servicesUp !== null && servicesUp >= 3 && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75" />
              )}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${statusDot}`} />
            </span>
            <span>
              {servicesUp === null
                ? 'Checking services…'
                : servicesUp >= 3
                ? 'All systems operational'
                : `${servicesUp}/4 services live`}
            </span>
          </div>
        </div>
      )}
    </AuthShell>
  )
}

export default LoginPage
